import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Store,
  Globe,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Clock,
  Star,
  Users,
} from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1776874882078-ca755aae66b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBwcmVtaXVtJTIwbGluZXMlMjBuZW9ufGVufDB8fHx8MTc3NzEwNjUwOXww&ixlib=rb-4.1.0&q=85";

const STAGE_DURATION = 4200;

const STAGES = [
  {
    key: "offline",
    label: "Day 0 · Offline",
    Icon: Store,
    title: "Local kirana",
    sub: "200 footfall · cash only",
    accent: "#FF9933",
    image: "/hero/offline.png",
    metrics: [
      { label: "Reach", value: "200" },
      { label: "Channel", value: "Walk-in" },
    ],
  },
  {
    key: "online",
    label: "Hour 48 · Online",
    Icon: Globe,
    title: "Digital storefront",
    sub: "Web · App · UPI live",
    accent: "#FFFFFF",
    image: "/hero/online.png",
    metrics: [
      { label: "Reach", value: "50K" },
      { label: "Orders", value: "Live" },
    ],
  },
  {
    key: "growth",
    label: "Day 90 · Growth",
    Icon: TrendingUp,
    title: "Bharat-scale revenue",
    sub: "5× MRR · 4.9★ rated",
    accent: "#00C853",
    image: "/hero/growth.png",
    metrics: [
      { label: "Revenue", value: "5×" },
      { label: "Rating", value: "4.9★" },
    ],
  },
];

const FloatingStat = ({ icon: Icon, label, value, accent, className, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`absolute z-20 dark:glass-dark glass-light rounded-2xl px-3.5 py-2.5 shadow-2xl flex items-center gap-2.5 ${className}`}
  >
    <span
      className="h-9 w-9 rounded-xl grid place-items-center shrink-0"
      style={{
        background: `${accent}1f`,
        border: `1px solid ${accent}55`,
      }}
    >
      <Icon className="h-4 w-4" style={{ color: accent }} />
    </span>
    <div className="leading-tight">
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
        {label}
      </div>
      <div className="text-sm font-heading font-bold tracking-tight">
        {value}
      </div>
    </div>
  </motion.div>
);

export const Hero = ({ onCTA }) => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStage((s) => (s + 1) % STAGES.length);
    }, STAGE_DURATION);
    return () => clearInterval(id);
  }, []);

  const current = STAGES[stage];
  const Icon = current.Icon;

  return (
    <section
      id="hero"
      className="relative min-h-[100vh] flex items-center pt-32 pb-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-25 dark:opacity-30 mix-blend-screen dark:mix-blend-lighten"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        <motion.div
          className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#FF7A00]/30 blur-[140px]"
          animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 -right-32 h-96 w-96 rounded-full bg-[#00C853]/20 blur-[140px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.6, 0.9, 0.6] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
        />
        <div className="absolute inset-0 grain opacity-[0.04] dark:opacity-[0.08] pointer-events-none" />
      </div>

      <div className="max-w-7xl mx-auto w-full px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: Copy */}
        <div className="lg:col-span-7 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full dark:glass-dark glass-light"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00C853] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00C853]" />
            </span>
            <span className="text-xs md:text-sm font-medium tracking-wide">
              Built for Bharat · Live in 48 hours
            </span>
          </motion.div>

          <div className="space-y-4">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-heading font-extrabold text-balance text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.02]"
              data-testid="hero-heading"
            >
              No website?
              <br />
              We craft your full
              <br />
              <span className="text-gradient-saffron">digital identity</span>
              <br />
              in <span className="text-gradient-green">48 hours.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed"
              data-testid="hero-subheading"
            >
              Websites, mobile apps and AI chat boards for India&apos;s fastest
              moving founders. From kirana to crore — we build the rails of
              your online business.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onCTA}
              className="group inline-flex items-center gap-3 h-14 pl-7 pr-3 rounded-full gradient-saffron text-white font-semibold text-base shadow-[0_12px_40px_rgba(255,122,0,0.45)] hover:shadow-[0_18px_60px_rgba(255,122,0,0.6)] hover:-translate-y-0.5 transition-all"
              data-testid="hero-cta-primary"
            >
              Start my 48-hour build
              <span className="h-9 w-9 grid place-items-center rounded-full bg-white/20 group-hover:bg-white/30 transition-all group-hover:translate-x-0.5">
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>

            <a
              href="#services"
              className="inline-flex items-center gap-2 h-14 px-6 rounded-full border border-border hover:bg-foreground/5 font-semibold text-sm transition-colors"
              data-testid="hero-cta-secondary"
            >
              <Sparkles className="h-4 w-4 text-[#FF9933]" />
              See what we build
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-2 text-sm text-muted-foreground"
            data-testid="hero-trust-strip"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#00C853]" />
              <span>48-hour delivery</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#FF9933]" />
              <span>200+ Bharat brands launched</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#00C853]" />
              <span>Avg 4.7x revenue lift</span>
            </div>
          </motion.div>
        </div>

        {/* Right: Animated transformation visual */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative w-full max-w-md mx-auto aspect-[4/5]"
            data-testid="hero-animation"
          >
            {/* Soft halo */}
            <motion.div
              aria-hidden
              className="absolute -inset-10 -z-10 rounded-[3rem] opacity-70 blur-3xl"
              style={{
                background:
                  "radial-gradient(60% 60% at 50% 50%, rgba(255,122,0,0.45) 0%, rgba(0,200,83,0.25) 60%, transparent 100%)",
              }}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Image stack */}
            <div className="absolute inset-0 rounded-[2rem] overflow-hidden border border-border shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={current.key}
                  src={current.image}
                  alt={current.title}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.04 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid={`hero-image-${current.key}`}
                />
              </AnimatePresence>

              {/* Gradient sheen */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40" />
              <motion.div
                aria-hidden
                className="absolute -inset-y-10 -left-1/3 w-1/3 rotate-12 bg-gradient-to-b from-transparent via-white/10 to-transparent pointer-events-none"
                animate={{ x: ["-30%", "320%"] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Top label */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`label-${current.key}`}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4 }}
                    className="px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold"
                    style={{
                      background: `${current.accent}26`,
                      color: current.accent,
                      border: `1px solid ${current.accent}55`,
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    {current.label}
                  </motion.div>
                </AnimatePresence>
                <div className="flex items-center gap-1">
                  {STAGES.map((s, i) => (
                    <button
                      key={s.key}
                      onClick={() => setStage(i)}
                      className="h-1 rounded-full transition-all"
                      style={{
                        width: i === stage ? 24 : 10,
                        background:
                          i === stage ? s.accent : "rgba(255,255,255,0.4)",
                      }}
                      data-testid={`hero-stage-${s.key}`}
                      aria-label={s.label}
                    />
                  ))}
                </div>
              </div>

              {/* Bottom detail card */}
              <div className="absolute bottom-4 left-4 right-4">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`details-${current.key}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="rounded-2xl p-4 backdrop-blur-xl bg-black/40 border border-white/10 shadow-2xl"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="h-11 w-11 rounded-xl grid place-items-center shrink-0"
                        style={{
                          background: `${current.accent}26`,
                          border: `1px solid ${current.accent}66`,
                        }}
                      >
                        <Icon
                          className="h-5 w-5"
                          style={{ color: current.accent }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-bold text-white text-base md:text-lg leading-tight">
                          {current.title}
                        </div>
                        <div className="text-xs md:text-sm text-white/70 mt-0.5">
                          {current.sub}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {current.metrics.map((m) => (
                        <div
                          key={m.label}
                          className="rounded-xl bg-white/5 border border-white/10 px-3 py-2"
                        >
                          <div className="text-[9px] uppercase tracking-[0.2em] text-white/50 font-semibold">
                            {m.label}
                          </div>
                          <div className="text-sm font-heading font-bold text-white">
                            {m.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Orbiting accent dot */}
              <motion.div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                animate={{ rotate: 360 }}
                transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              >
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 h-2 w-2 rounded-full"
                  style={{
                    background: current.accent,
                    boxShadow: `0 0 14px ${current.accent}, 0 0 28px ${current.accent}99`,
                  }}
                />
              </motion.div>
            </div>

            {/* Floating side stats */}
            <FloatingStat
              icon={Star}
              label="Rating"
              value="4.9 · 220 reviews"
              accent="#FF9933"
              className="-left-4 md:-left-8 top-8"
              delay={0.6}
            />
            <FloatingStat
              icon={Users}
              label="Live builds"
              value="38 this month"
              accent="#00C853"
              className="-right-2 md:-right-6 top-1/3"
              delay={0.85}
            />
            <FloatingStat
              icon={TrendingUp}
              label="Revenue lift"
              value="4.7× in 90d"
              accent="#FF7A00"
              className="-right-2 md:-right-10 bottom-8"
              delay={1.1}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
